<script setup>
import {useRestaurantsStore, useUserGroupsStore, useUsersStore} from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useDesignSystem } from '@/composables/useDesignSystem'
import moment from 'moment-timezone'
import Tooltip from '@/layouts/components/Tooltip.vue'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import ContactAppendsEditor from '@core/components/ContactAppendsEditor.vue'
import {generateGeneralReservationUrlWithQueryString, numberWithCommas} from '@/plugins/misc'
import { cleanContactEntries } from '@/plugins/contactEntries'
import {
  emailValidator,
  phoneValidator, profileIdValidator,
  requiredValidator,
  reservationPlatformUrlValidator
} from '@validators'
import imagePlaceholder from "@/assets/images/imagePlaceholder.webp"
import googleAttribution from "@/assets/images/ui/google_on_white.png"
import {VForm} from "vuetify/components"
import {storeToRefs} from "pinia"
import {ref} from "vue"
const emit = defineEmits([
  'update:modelValue',
  'submit',
  'update:isDialogVisible'
])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const usersStore = useUsersStore()
let adminEditingUser = null
let myUser = null;
({ myUser } = storeToRefs(usersStore))


const userGroupsStore = useUserGroupsStore()
let restaurantUserGroups = null;
({ restaurantUserGroups  } = storeToRefs(userGroupsStore))


const restaurantsStore = useRestaurantsStore()
let restaurants = null
let groups = null
let markets = null
let timeZones = null
let restaurantEditingRestaurant = null;
({ groups, markets, restaurants, restaurantEditingRestaurant, timeZones  } = storeToRefs(restaurantsStore))

const isDialogVisible = ref(false)
const isConfirmDialogOpen = ref(false)
const isConfirmSmsDialogOpen = ref(false)
const isInfoDialogVisible = ref(false)
const choosingImages = ref(false)
const currentImage = ref(0)

//save the original value of restaurantEditingRestaurant.value.generalReservationPhoneNumberAppends once loaded
let originalGeneralReservationPhoneNumberAppends = null
watch(restaurantEditingRestaurant, newValue => {
  if(newValue){
    originalGeneralReservationPhoneNumberAppends = newValue.generalReservationPhoneNumberAppends
  }
})

const form = ref()
const refVForm = ref()

const submitted = ref(false)
const valid = ref(true)
const changesMade = ref(true)

const tab = ref('Contacts')

// Form Errors
const errors = ref({
  profileId: undefined,
})

onUpdated(async () => {
  useReservationSpendLimitsForPrivate.value = (restaurantEditingRestaurant.value?.brunchLunchMinSpendPerPerson == restaurantEditingRestaurant.value?.privateBrunchLunchMinSpendPerPerson && restaurantEditingRestaurant.value?.dinnerSunThruThursMinSpendPerPerson == restaurantEditingRestaurant.value?.privateDinnerSunThruThursMinSpendPerPerson && restaurantEditingRestaurant.value?.dinnerFriAndSatMinSpendPerPerson == restaurantEditingRestaurant.value?.privateDinnerFriAndSatMinSpendPerPerson)
})

const clearErrors = () => {
  if (errors.value) {
    errors.value = {}
  }
}

const smsConfirmationMessage = `By clicking Yes below, you agree to receive automated text messages from RM to the phone number(s) you provided.
        These messages will contain information and links pertaining to reservation requests made by our members.
        We do not charge restaurants a fee, and consent is not a condition of purchase. Msg and data rates may apply. Msg frequency varies based on member demand.
        You may unsubscribe at any time by replying STOP to any text message, or by removing your phone number from the same form field where you provided it.
        Please see our <a href="/privacy" target="_blank">Privacy Policy</a> and <a href="/terms" target="_blank">Terms</a> for more information.`

const checkBoxChecked = ref(true)

const formListingType = ref(restaurantEditingRestaurant.value?.acceptingReferrals ? (restaurantEditingRestaurant.value?.status == "Preferred" ? 'Preferred' : 'Favorite' ) : 'Opt out')
const formHoldsType = ref(restaurantEditingRestaurant.value?.generalReservationHoldRequired ? restaurantEditingRestaurant.value?.generalReservationHoldType : null)
const useReservationSpendLimitsForPrivate = ref(restaurantEditingRestaurant.value?.brunchLunchMinSpendPerPerson == restaurantEditingRestaurant.value?.privateBrunchLunchMinSpendPerPerson && restaurantEditingRestaurant.value?.dinnerSunThruThursMinSpendPerPerson == restaurantEditingRestaurant.value?.privateDinnerSunThruThursMinSpendPerPerson && restaurantEditingRestaurant.value?.dinnerFriAndSatMinSpendPerPerson == restaurantEditingRestaurant.value?.privateDinnerFriAndSatMinSpendPerPerson)

const bookingWindowGuidance = computed(() => {
  const raw = restaurantEditingRestaurant.value?.reservationBookingWindowDays
  const n = Number(raw)
  if (!raw || !Number.isInteger(n) || n <= 0) {
    return 'Members can request any date while their membership is active.'
  }
  const tz = restaurantEditingRestaurant.value?.timeZoneIdentifier || 'America/Chicago'
  const lastDate = moment.tz(tz).add(n - 1, 'days').format('ddd, MMM D')
  
  return `With ${n} days, members can request through ${lastDate} — today counts as day 1. The window slides forward each day.`
})

const setPrivateSpendLimitsToSameAsGeneral = () => {
  restaurantEditingRestaurant.value.privateBrunchLunchMinSpendPerPerson = restaurantEditingRestaurant.value.brunchLunchMinSpendPerPerson
  restaurantEditingRestaurant.value.privateDinnerSunThruThursMinSpendPerPerson = restaurantEditingRestaurant.value.dinnerSunThruThursMinSpendPerPerson
  restaurantEditingRestaurant.value.privateDinnerFriAndSatMinSpendPerPerson = restaurantEditingRestaurant.value.dinnerFriAndSatMinSpendPerPerson
}

const onlyRestaurantInMarket = computed(() => {
  let restaurantsInMarket = restaurants.value.filter(restaurant => restaurant.market == restaurantEditingRestaurant.value.market)
  
  return restaurantsInMarket.length == 1
})

const nameIsNotUnique = computed(() => {
  let restaurantsInMarket = restaurants.value.filter(restaurant => restaurant.name == restaurantEditingRestaurant.value.name)
  
  return restaurantsInMarket.length > 1
})

const exampleGeneralReservationUrl = computed(() => {
  // Guard against a null/absent URL — calling .toLowerCase() on it threw during render.
  const url = restaurantEditingRestaurant?.value?.generalReservationUrl?.toLowerCase() || ''
  if(url.includes('opentable.com')){
    return `https://www.opentable.com/r/restaurant-city`
  }
  if(url.includes('tock.com')){
    return `https://www.exploretock.com/restaurantname`
  }
  if(url.includes('resy.com')){
    return `https://resy.com/cities/city/restaurant-name`
  }
  if(url.includes('sevenrooms.com')){
    return `https://www.sevenrooms.com/reservations/restaurantname`
  }
  if(url.includes('yelp.com')){
    return `https://www.yelp.com/reservations/restaurant-name`
  }

  return 'https://www.opentable.com/r/restaurant-city'
})

const onFormSubmit = () => {
  refVForm.value?.validate().then(async ({valid: isValid}) => {
    if (isValid){
      await usersStore.showLoadingOverlay()
      if(formListingType.value == 'Opt out')
        restaurantEditingRestaurant.value.acceptingReferrals = false
      else{
        restaurantEditingRestaurant.value.acceptingReferrals = true
        restaurantEditingRestaurant.value.status = (formListingType.value == "Preferred" ? 'Preferred' : 'Member Favorite' )
      }
      if(formHoldsType.value == null)
        restaurantEditingRestaurant.value.generalReservationHoldRequired = false
      else{
        restaurantEditingRestaurant.value.generalReservationHoldRequired = true
        restaurantEditingRestaurant.value.generalReservationHoldType = formHoldsType.value
      }

      // RM-189 — normalize append fields to clean [{ name, email|phone }] arrays
      // (drops blank rows; coerces any legacy comma-String) before saving.
      const r = restaurantEditingRestaurant.value
      r.generalReservationEmailAppends = cleanContactEntries(r.generalReservationEmailAppends, 'email')
      r.generalReservationPhoneNumberAppends = cleanContactEntries(r.generalReservationPhoneNumberAppends, 'phone')
      r.privateReservationEmailAppends = cleanContactEntries(r.privateReservationEmailAppends, 'email')

      await restaurantsStore.saveRestaurantEditingRestaurant()
      submitted.value = true
      await usersStore.hideLoadingOverlay()
    } else {
      await nextTick() // Wait for the next DOM update cycle

      // Now attempt to find the first invalid field
      const firstInvalidField = document.querySelector('.v-input--error')

      if (firstInvalidField) {
        // Scroll the invalid field into view
        firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // If it's a Vuetify input component, focus the input element
        const inputElement = firstInvalidField.querySelector('input, textarea, .v-select__slot')
        if (inputElement) {
          inputElement.focus()
        }
      } else {
        console.log('No invalid field found after waiting for DOM update.')
      }
    }
  })
}

const requestClose = () => {
  // isConfirmDialogOpen.value = true
  if (changesMade.value) {
    isConfirmDialogOpen.value = true
  }
  else{
    restaurantsStore.cancelRestaurantEditRestaurant()
    isDialogVisible.value = false
  }
}

const handleMediaSelection = selectedMedia => {
  if(selectedMedia){
    //rips out any ?t=9287439832749 that may have been added to refresh the image after cropping
    // adminEditingUserGroup.value.profileImageId = selectedMedia.imageId.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/)[0];
    restaurantEditingRestaurant.value.imageIds = selectedMedia
    choosingImages.value = false
  } else{
    restaurantEditingRestaurant.value.imageIds = null

    //choosingProfileImage.value = false
  }
}

//check to see if we should pop up any dialogs when the store changes
//this seems hacky
restaurantsStore.$subscribe((mutation, state) => {
  isDialogVisible.value = ( state.restaurantEditingRestaurant != null)
})

const restaurantUserGroup = computed(() => {
  //in restaurantUserGroups find a group where the props.restaurant._id matches and then in that groups userPermissions array find obejct that matches for email = myUser.value.email then return the "permission" key value in the same object that matched the email
  if(restaurantUserGroups.value?.length > 0){
    return restaurantUserGroups.value.find(group => group?._id == restaurantEditingRestaurant.value?.restaurantUserGroup)
  }
})

const dialogRef = ref()
const scrollToBottom = () => {
  console.log("trying to scroll")
  nextTick(() => {
    if (dialogRef.value) {
      console.log("trying to scroll2", dialogRef.value)
      dialogRef.value.scrollTop = dialogRef.value.scrollHeight
    }
  })
}

watch(
  () => restaurantEditingRestaurant?.value?.acceptingReferrals,
  () => {
    if(restaurantEditingRestaurant?.value?.acceptingReferrals){
      formListingType.value = restaurantEditingRestaurant?.value?.status
    }
  },
  {deep: true}
)

watch(
  [
    () => restaurantEditingRestaurant?.value?.generalReservationHoldRequired,
    () => restaurantEditingRestaurant?.value?.generalReservationHoldType
  ],
  ([newValHoldRequired, newValHoldType], [oldValHoldRequired, oldValHoldType]) => {
    formHoldsType.value = newValHoldRequired ? newValHoldType : null
  },
  { deep: true, immediate: true }
)
</script>

<template>
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmDialogOpen"
    confirmation-msg="Save changes?"
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="restaurantsStore.saveRestaurantEditingRestaurant(); isDialogVisible = false;"
    @cancel="restaurantsStore.cancelRestaurantEditRestaurant(); isDialogVisible = false;"
  />
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmSmsDialogOpen"
    :confirmation-msg="smsConfirmationMessage"
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="restaurantsStore.saveRestaurantEditingRestaurant(); isDialogVisible = false;"
  />
  <MediaChooserAndManager
    v-model:is-dialog-visible="choosingImages"
    title="Add/select pics"
    :preselected-media-ids="restaurantEditingRestaurant?.imageIds"
    multi-select
    :restaurant="restaurantEditingRestaurant"
    :crop="false"
    @media-selected="handleMediaSelection"
  />

  <VDialog
    v-if="restaurantEditingRestaurant && (myUser.userLevel == 'admin' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'owner')"
    ref="dialogRef"
    v-model="isDialogVisible"
    :width="dWidth"
    persistent
    fullscreen
  >
    <VToolbar
      dark
      color="primary"
      class="toolbar-header"
    >
      <VToolbarItems style="" />
      <VSpacer />
      <VToolbarTitle>Settings</VToolbarTitle>
      <VSpacer />
      <!--      <v-toolbar-items> -->
      <!--        <v-btn -->
      <!--          icon="$close" -->
      <!--          size="large" -->
      <!--          variant="text" -->
      <!--          @click="requestClose" -->
      <!--        > -->
      <!--          <v-icon>mdi-close</v-icon> -->
      <!--        </v-btn> -->
      <!--      </v-toolbar-items> -->
    </VToolbar>


    <VCard v-if="restaurantEditingRestaurant">
      <VCardText class="pt-16">
        <template v-if="restaurantEditingRestaurant">
          <p class="mt-4 text-center">
            <span><VIcon
              v-if="restaurantEditingRestaurant.status == 'Preferred'"
              style="margin-left: -3px; margin-top: -3px;"
            >iconamoon:certificate-badge-light</VIcon>{{ restaurantEditingRestaurant.name }}</span><br>
            {{ restaurantEditingRestaurant.googlePlaceData.vicinity }}<br>
            Tier: <span
              class="text-primary cursor-pointer"
              @click="tab='Tier'"
            >{{ formListingType }}</span>
          </p>


          <VForm
            ref="refVForm"
            v-model="valid"
            validate-on="blur"
            @submit.prevent="onFormSubmit"
          >
            <VTabs
              v-model="tab"
              class="tab-height"
              center-active
            >
              <VTab
                value="Contacts"
                class="tab-height pl-2 pr-4"
              >
                Contacts
              </VTab>
              <VTab
                value="Tier"
                class="tab-height px-4"
              >
                Tier
              </VTab>
              <VTab
                value="Profile"
                class="tab-height px-4"
              >
                Profile/Pics
              </VTab>
              <VTab
                v-if="formListingType != 'Opt out'"
                value="Spends/Holds"
                class="tab-height pl-4 pr-2"
              >
                Spends/Holds
              </VTab>
            </VTabs>

            <VWindow
              v-model="tab"
              class="pb-16"
            >
              <VWindowItem
                class="text-sm"
                value="Contacts"
              >
                <VCol
                  cols="12 pb-0"
                >
                  <h4 class="text-center ">
                    Admins/Owner
                  </h4>

                  <!--  v-for Loop through restaurantUserGroups?.userPermissions and show email, permission for each -->
                  <div
                    v-for="user in restaurantUserGroup?.userPermissions"
                    :key="user.email"
                  >
                    <div class="user-card">
                      <div v-if="user.firstName && user.lastName">
                        {{ user.firstName }} {{ user.lastName }}
                      </div>
                      <!--                    <div> -->
                      <!--                      <span v-if="user.permission == 'owner'">Platinum + Owner</span> -->
                      <!--                      <span v-else-if="user.permission == 'admin'">Platinum + Admin</span> -->
                      <!--                      <span v-else> {{user.permission}}</span> -->
                      <!--                    </div> -->
                      <div
                        v-if="user?.emailConfirmed"
                        class="text-xs"
                      >
                        {{ user.email }}
                      </div>
                      <div v-else>
                        {{ user.email }}<span v-if="user?.dateInvited"> - invited {{ moment(user.dateInvited).format("M/D") }}</span><br>
                        <span class="text-xs">needs to create an account here: <a
                          href="https://reserverm.com/"
                          target="_blank"
                        >reserverm.com</a></span>
                      </div>
                    </div>
                  </div>
                  <div class="text-center text-xs">
                    to add more, email <a
                      href="mailto:preferred@reserverm.com"
                      target="_blank"
                    >preferred@reserverm.com</a>
                  </div>
                </VCol>

                <VCol
                  cols="12"
                >
                  <h4 class="text-center mt-4 mb-4">
                    Reservation Referrals
                  </h4>
                  <ContactAppendsEditor
                    v-model="restaurantEditingRestaurant.generalReservationEmailAppends"
                    value-key="email"
                    value-label="Email"
                    value-type="email"
                    add-label="Add email"
                    require-at-least-one
                  />
                  <ContactAppendsEditor
                    v-model="restaurantEditingRestaurant.generalReservationPhoneNumberAppends"
                    value-key="phone"
                    value-label="Cell phone"
                    value-type="tel"
                    add-label="Add phone number"
                    hint="Texted when a referral comes in — (312) 399-5527 format"
                    class="mt-4"
                  />
                  <h4 class="text-center mt-4 mb-4">
                    Event Inquiries
                  </h4>
                  <ContactAppendsEditor
                    v-model="restaurantEditingRestaurant.privateReservationEmailAppends"
                    value-key="email"
                    value-label="Email"
                    value-type="email"
                    add-label="Add email"
                    require-at-least-one
                  />
                </VCol>
              </VWindowItem>
              <VWindowItem
                class="text-sm"
                value="Tier"
              >
                <VCol
                  cols="12"
                >
                  <VSelect
                    v-show="false"
                    v-model="formListingType"
                    label="listing type"
                    item-title="text"
                    item-value="value"
                    :items="[{value: 'Favorite', text:'Favorite (default)'}, {value: 'Preferred', text:'Preferred (request upgrade)'}, {value: 'Opt out', text:'Opt Out'}]"
                    :rules="[requiredValidator]"
                    required
                  />
                  <div>
                    <VCardText class="pa-0">
                      <div class="flex">
                        <div
                          class="align-self-center mr-2"
                          style="width:50px;"
                        >
                          <VIcon
                            v-if="formListingType=='Preferred'"
                            icon="mdi-check-circle"
                            class="check-icon"
                            size="40"
                          />
                        </div>
                        <div
                          :class="{'highlight' : formListingType=='Preferred'}"
                          class="selection-item"
                          @click="formListingType='Preferred'"
                        >
                          <b><VIcon style="margin-left: -3px;">iconamoon:certificate-badge-light</VIcon>Preferred (upgraded)</b><br>
                          member added & upgraded<br>
                          • featured listing<br>
                          • high profile/spend referrals<br>
                          • zero cost & no commitment<br>
                          • post happenings: experiences, special offerings, updates & more<br>
                          • showcase event spaces (group/private)<br>
                          • free Platinum membership<br>
                          <div class="mt-2">
                            <i>review referrals within 24 hours - accept only if appealing</i>
                          </div>
                        </div>
                      </div>
                      <div class="flex">
                        <div
                          class="align-self-center mr-2"
                          style="width:50px;"
                        >
                          <VIcon
                            v-if="formListingType=='Favorite'"
                            icon="mdi-check-circle"
                            class="check-icon"
                            size="40"
                          />
                        </div>
                        <div
                          :class="{'highlight' : formListingType=='Favorite'}"
                          class="selection-item"
                          @click="formListingType='Favorite'"
                        >
                          <b>Favorite</b><br>
                          once added by member<br>
                          • high profile/spend referrals<br>
                          • zero cost & no commitment<br>
                          <div class="mt-2">
                            <i>receive referrals to review</i>
                          </div>
                        </div>
                      </div>
                      <div class="flex">
                        <div
                          class="align-self-center mr-2"
                          style="width:50px;"
                        >
                          <VIcon
                            v-if="formListingType=='Opt out'"
                            icon="mdi-check-circle"
                            class="check-icon"
                            size="40"
                          />
                        </div>
                        <div
                          :class="{'highlight' : formListingType=='Opt out'}"
                          class="selection-item"
                          @click="formListingType='Opt out'"
                        >
                          <b>Opt out</b><br>
                          • members can’t submit request referrals for your review, can only favorite you<br>
                        </div>
                      </div>
                    </VCardText>
                  </div>
                </VCol>
              </VWindowItem>
              <VWindowItem
                class="text-sm"
                value="Profile"
              >
                <VCol
                  cols="12"
                >
                  <VTextarea
                    v-model="restaurantEditingRestaurant.description"
                    label="Brief Overview"
                    autocomplete="null"
                    hint="Viewable by Members who have Favorited you & visitors in general "
                    persistent-hint
                    class="mt-4 mb-2"
                    rows="2"
                    auto-grow
                  />
                  <div class="text-sm mb-2">
                    <span v-if="restaurantEditingRestaurant.imageIds?.length > 0">
                      Your Profile Pics ({{ restaurantEditingRestaurant.imageIds?.length }})
                    </span>
                    <span v-else>
                      Your Profile Pics
                    </span>
                  </div>
                  <VCarousel
                    v-model="currentImage"
                    height="20dvh"
                    hide-delimiters
                  >
                    <template #prev="{ props }">
                      <div
                        v-if="restaurantEditingRestaurant.imageIds?.length > 1"
                        class="icon-wrapper"
                      >
                        <VIcon
                          color="white"
                          variant="elevated"
                          @click="props.onClick"
                        >
                          mdi-chevron-left
                        </VIcon>
                      </div>
                    </template>
                    <template #next="{ props }">
                      <div
                        v-if="restaurantEditingRestaurant.imageIds?.length > 1"
                        class="icon-wrapper"
                      >
                        <VIcon
                          color="white"
                          variant="elevated"
                          @click="props.onClick"
                        >
                          mdi-chevron-right
                        </VIcon>
                      </div>
                    </template>
                    <template v-if="restaurantEditingRestaurant.imageIds?.length > 0">
                      <VCarouselItem
                        v-for="(photo, index) in restaurantEditingRestaurant.imageIds"
                        :key="index"
                        :src="mediaUrl(photo, 'small')"
                        :value="photo"
                        contain
                      />
                    </template>
                    <template v-else>
                      <VCarouselItem
                        :src="imagePlaceholder"
                        contain
                      />
                    </template>
                  </VCarousel>

                  <div class="avatar-fallback flex flex-row align-center justify-center">
                    <span
                      class="text-sm text-primary cursor-pointer"
                      @click="choosingImages = true"
                    >+add/select pics to display</span>
                  </div>
                </VCol>
                <VCol
                  cols="12"
                >
                  <VSelect
                    v-model="restaurantEditingRestaurant.generalReservationPlatform"
                    label="Primary Reservation Platform"
                    item-title="text"
                    item-value="value"
                    :items="[{text: 'Not Set', value: ''},{text: 'OpenTable', value: 'OpenTable'}, {text: 'SevenRooms', value: 'SevenRooms'}, {text: 'Resy', value: 'Resy'}, {text: 'Tock', value: 'Tock'}, {text: 'Yelp', value: 'Yelp'}, {text: 'Other', value: 'Other'}]"
                    hint="The primary system used to manage reservations."
                  />
                  <VTextField
                    v-model="restaurantEditingRestaurant.generalReservationUrl"
                    label="Base Reservations Url"
                    autocomplete="null"
                    :rules="[reservationPlatformUrlValidator]"
                    :hint="`e.g. ${exampleGeneralReservationUrl}`"
                  />


                  <div
                    v-if=" generateGeneralReservationUrlWithQueryString(restaurantEditingRestaurant)"
                    class="text-caption"
                  >
                    Test Url: <a
                      :href="generateGeneralReservationUrlWithQueryString(restaurantEditingRestaurant)"
                      target="_blank"
                    >{{ generateGeneralReservationUrlWithQueryString(restaurantEditingRestaurant) }}</a>
                  </div>
                </VCol>
              </VWindowItem>
              <VWindowItem
                class="text-sm"
                value="Spends/Holds"
              >
                <h4 class="text-center ">
                  Guaranteed minimum F&B spends per person (pp) for members to submit request referrals
                </h4>
                <VCardText
                  class="pa-2 text-xs"
                  style="line-height: 1.1em;"
                >
                  <ul style="list-style-type: disc; margin-left: 20px;">
                    <li style="margin-bottom: 5px;">
                      You’ll only receive referrals for spends that meet/exceed the levels below
                    </li>
                    <li style="margin-bottom: 5px;">
                      Member's credit card held if request referral accepted for full spend: F&B minimum + tip (20%) + tax
                      <ul style="list-style-type: circle; margin-left: 20px;">
                        <li>If the member does not meet/exceed the minimum, the restaurant can charge a flat fee on the bill or notify RM within 24 hours to receive the full remaining hold total (including due to late cancellation or no show)</li>
                      </ul>
                    </li>
                    <li style="margin-bottom: 5px;">
                      Restaurants can always waive hold/spend at their sole discretion on a per request basis
                    </li>
                    <li>
                      RM does not take any fee nor commission on spends from restaurants nor members
                    </li>
                  </ul>
                </VCardText>


                <VCol
                  cols="12"
                >
                  <h4 class="text-center mb-4">
                    Reservations
                  </h4>
                  <VSelect
                    v-model="restaurantEditingRestaurant.brunchLunchMinSpendPerPerson"
                    label="Prior to 3pm (Daily)"
                    item-title="text"
                    item-value="value"
                    :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100},{text:'$75', value: 75},{text:'$50', value: 50}]"
                    :rules="[requiredValidator]"
                    required
                    :hint="`${restaurantEditingRestaurant.name} will only receive requests at or above this amount.`"
                    class="mb-2"
                  />
                  <VSelect
                    v-model="restaurantEditingRestaurant.dinnerSunThruThursMinSpendPerPerson"
                    label="After 3pm (Sun-Thurs)"
                    item-title="text"
                    item-value="value"
                    :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100},{text:'$75', value: 75},{text:'$50', value: 50}]"
                    :rules="[requiredValidator]"
                    required
                    :hint="`${restaurantEditingRestaurant.name} will only receive requests at or above this amount.`"
                    class="mb-2"
                  />
                  <VSelect
                    v-model="restaurantEditingRestaurant.dinnerFriAndSatMinSpendPerPerson"
                    label="After 3pm (Fri & Sat)"
                    item-title="text"
                    item-value="value"
                    :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100}]"
                    :rules="[requiredValidator]"
                    required
                    :hint="`${restaurantEditingRestaurant.name} will only receive requests at or above this amount.`"
                    class="mb-2"
                  />
                  <VSelect
                    v-model="restaurantEditingRestaurant.maxGeneralReservationPartySize"
                    label="Maximum party size for reservations"
                    item-title="text"
                    item-value="value"
                    :items="[1,2,3,4,5,6,7,8,9,10,11,12,13,14]"
                    :rules="[requiredValidator]"
                    required
                    :hint="`${restaurantEditingRestaurant.name} will only receive requests at or below this number.`"
                  />
                  <VTextField
                    v-model.number="restaurantEditingRestaurant.reservationBookingWindowDays"
                    label="Booking window (days members can request out)"
                    type="number"
                    min="1"
                    placeholder="No limit"
                    :hint="bookingWindowGuidance"
                    persistent-hint
                    class="mt-2"
                    style="font-size: 16px;"
                  />
                  <template v-if="formHoldsType=='full'">
                    <VSelect
                      v-model="restaurantEditingRestaurant.generalReservationHoldCancellationLimitHours"
                      label="Cancellation Notice Period"
                      item-title="text"
                      item-value="value"
                      :items="[{text:'4 hours', value: 4} ,{text:'8 hours', value: 8} ,{text:'12 hours', value: 12} ,{text:'24 hours', value: 24} ,{text:'48 hours', value: 48}]"
                      :rules="[requiredValidator]"
                      required
                      hint="If a reservation is canceled before this period, the credit card hold is fully released."
                    />
                    <VTextField
                      v-show="false"
                      v-model="restaurantEditingRestaurant.generalReservationHoldTaxPercentage"
                      class="mt-2"
                      label="Sales Tax Rate"
                      hint="The combined applicable state and local sales/use tax to be added to the credit card hold."
                      type="number"
                      suffix="%"
                    />

                    <VSelect
                      v-show="false"
                      v-model="restaurantEditingRestaurant.generalReservationCCFeePercentage"
                      label="Members Credit Card Transaction Fee"
                      hint="~3% credit card transaction fee is withheld from ACH transfer to you. Here you can pass all/part of that fee on to the member."
                      item-title="text"
                      item-value="value"
                      :items="[{text:'None', value: 0},{text:'1.5%', value: 1.5},{text:'2%', value: 2},{text:'2.5%', value: 2.5},{text:'3%', value: 3}]"
                      :rules="[requiredValidator]"
                      required
                      class="mt-4"
                    />
                  </template>

                  <h4 class="text-center mb-4">
                    Events
                  </h4>
                  <VCheckbox
                    v-model="useReservationSpendLimitsForPrivate"
                    label="Use above Reservation minimum spends for Events"
                    class="mb-2 text-sm"
                    @change="newValue => newValue && setPrivateSpendLimitsToSameAsGeneral()"
                    @click="scrollToBottom"
                  />
                  <VSelect
                    v-if="!useReservationSpendLimitsForPrivate"
                    v-model="restaurantEditingRestaurant.privateBrunchLunchMinSpendPerPerson"
                    label="Prior to 3pm (Daily)"
                    item-title="text"
                    item-value="value"
                    :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100},{text:'$75', value: 75},{text:'$50', value: 50}]"
                    :rules="[requiredValidator]"
                    required
                    hint="The restaurant will only receive requests at or above this amount."
                    class="mb-2"
                  />
                  <VSelect
                    v-if="!useReservationSpendLimitsForPrivate"
                    v-model="restaurantEditingRestaurant.privateDinnerSunThruThursMinSpendPerPerson"
                    label="After 3pm (Sun-Thurs)"
                    item-title="text"
                    item-value="value"
                    :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100}]"
                    :rules="[requiredValidator]"
                    required
                    hint="The restaurant will only receive requests at or above this amount."
                    class="mb-2"
                  />
                  <VSelect
                    v-if="!useReservationSpendLimitsForPrivate"
                    v-model="restaurantEditingRestaurant.privateDinnerFriAndSatMinSpendPerPerson"
                    label="After 3pm (Fri & Sat)"
                    item-title="text"
                    item-value="value"
                    :items="[{text:'$2,500', value: 2500} ,{text:'$2,000', value: 2000} ,{text:'$1,500', value: 1500} ,{text:'$1,000', value: 1000} ,{text:'$900', value: 900} ,{text:'$800', value: 800} ,{text:'$750', value: 750} ,{text:'$700', value: 700}, {text:'$650', value: 650}, {text:'$600', value: 600}, {text:'$550', value: 550}, {text:'$500', value: 500}, {text:'$450', value: 450}, {text:'$400', value: 400}, {text:'$375', value: 375}, {text:'$350', value: 350}, {text:'$300', value: 300},{text:'$250', value: 250},{text:'$200', value: 200},{text:'$150', value: 150},{text:'$125', value: 125},{text:'$100', value: 100}]"
                    :rules="[requiredValidator]"
                    required
                    hint="The restaurant will only receive requests at or above this amount."
                    class="mb-2"
                  />
                </VCol>
                <template v-show="false">
                  <VCol
                    cols="12"
                  >
                    <VSelect
                      v-model="formHoldsType"
                      label="listing type"
                      item-title="text"
                      item-value="value"
                      :items="[{value: 'full', text:'Full Hold'}, {value: 'No Holds', text:'No Holds'}]"
                    />
                  </VCol>
                </template>
              </VWindowItem>
            </VWindow>





            <div
              class="action-buttons d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                type="submit"
              >
                Save
              </VBtn>

              <VBtn
                color="secondary"
                variant="tonal"
                @click="requestClose"
              >
                Cancel
              </VBtn>
            </div>
          </VForm>
        </template>
      </VCardText>
    </VCard>
  </VDialog>

  <VDialog
    max-width="500"
    :model-value="isInfoDialogVisible"
  >
    <VCard class="text-center px-5 py-4 pb-0 d-inline">
      <PreferredContent :show-search="false" />
    </VCard>
    <VBtn
      icon="$close"
      size="large"
      variant="text"
      color="secondary"
      class="dialog-close-btn"
      @click="isInfoDialogVisible = false"
    >
      <VIcon>mdi-close</VIcon>
    </VBtn>
  </VDialog>
</template>

<style lang="scss" scoped>
.icon-wrapper {
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: var(--ds-radius-full);
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

  .action-buttons {
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
    z-index: var(--ds-z-dropdown);
    background: var(--ds-surface-card);
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    border-top: thin solid var(--ds-border-default);
    padding: 1rem;
    box-shadow: 0px -3px 6px rgba(0,0,0,0.025);
  }

  .v-selection-control-group {
    display: flex;
    flex-direction: unset;
  }

  .v-toolbar-title{
    flex: unset;
  }

  .toolbar-header{
    position: fixed;
    z-index: var(--ds-z-modal);
  }

  .main-content{
    top: 60px;
    position: relative;
    padding-bottom: 60px !important;
  }

  .highlight {
    background-color: rgb(69, 181, 169) !important;
    color: #2a2929;
  }
  .selection-item {
    background-color: rgb(222, 222, 222);
    padding: 10px;
    margin: 0;
    margin-bottom: 1em;
    cursor: pointer;
    width: -webkit-fill-available;
    border-radius: var(--ds-radius-xs);
  }

  .check-icon{
    color: rgb(69, 181, 169);
    margin-right: 3px;
    margin-bottom: 3px;
  }

  .user-card {
    line-height: 1em;
    padding: var(--ds-space-1);
    margin: 5px;
    border-bottom: 1px solid var(--ds-border-default);
  }
</style>
